import cx from "classnames";
import { ReactNode, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { Cross, External, Pin, ChevronDown } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import { AssistantPanelMode } from "components/AssistantPanel/Layout/types";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import DropdownMenu from "ds/components/DropdownMenu";
import Button from "ds/components/Button";

import styles from "./styles.module.css";
import useFloatingDrag from "../useFloatingDrag";
import useAssistantPanelStore from "../useStore";
import { ASSISTANT_LABELS } from "./constants";

type AssistantPanelLayoutHeaderProps = {
  children?: ReactNode;
  onModeChange?: (mode: AssistantPanelMode) => void;
  onPinChange?: (isPinned: boolean) => void;
};

const AssistantPanelLayoutHeader = ({
  children,
  onModeChange,
  onPinChange,
}: AssistantPanelLayoutHeaderProps) => {
  const { variant, setIsOpen, pin, unpin, switchMode, mode } = useAssistantPanelStore(
    useShallow((state) => ({
      variant: state.variant,
      setIsOpen: state.setIsOpen,
      pin: state.pin,
      unpin: state.unpin,
      switchMode: state.switchMode,
      mode: state.mode,
    }))
  );
  const { onDragStart } = useFloatingDrag();

  const onHeaderPointerStart = variant === "floating" ? onDragStart : undefined;

  const onHome = useCallback(() => {
    onModeChange?.(AssistantPanelMode.HOME);
    switchMode(AssistantPanelMode.HOME);
  }, [switchMode, onModeChange]);

  const onAiChat = useCallback(() => {
    onModeChange?.(AssistantPanelMode.AI_CHAT);
    switchMode(AssistantPanelMode.AI_CHAT);
  }, [switchMode, onModeChange]);

  const onLearn = useCallback(() => {
    onModeChange?.(AssistantPanelMode.LEARN);
    switchMode(AssistantPanelMode.LEARN);
  }, [switchMode, onModeChange]);

  const handlePin = useCallback(() => {
    onPinChange?.(true);
    pin();
  }, [pin, onPinChange]);

  const handleUnpin = useCallback(() => {
    onPinChange?.(false);
    unpin();
  }, [unpin, onPinChange]);

  return (
    <Box
      padding="x-large"
      gap="medium"
      justify="between"
      align="center"
      onPointerDown={onHeaderPointerStart}
      className={cx(styles.header, { [styles.floating]: variant === "floating" })}
    >
      <Box>{children}</Box>
      <Box justify="end" gap="medium">
        {mode !== AssistantPanelMode.HOME && (
          <DropdownMenu
            triggerComponent={
              <Button endIcon={ChevronDown} variant="ghost" size="small">
                {ASSISTANT_LABELS[mode]}
              </Button>
            }
          >
            <DropdownMenuItem onAction={onHome}>
              {ASSISTANT_LABELS[AssistantPanelMode.HOME]}
            </DropdownMenuItem>

            {mode !== AssistantPanelMode.AI_CHAT && (
              <DropdownMenuItem onAction={onAiChat}>
                {ASSISTANT_LABELS[AssistantPanelMode.AI_CHAT]}
              </DropdownMenuItem>
            )}
            {mode !== AssistantPanelMode.LEARN && (
              <DropdownMenuItem onAction={onLearn}>
                {ASSISTANT_LABELS[AssistantPanelMode.LEARN]}
              </DropdownMenuItem>
            )}
          </DropdownMenu>
        )}

        {variant === "floating" && (
          <ButtonIcon icon={Pin} variant="ghost" size="small" onPress={handlePin}>
            Pin to sidebar
          </ButtonIcon>
        )}
        {variant === "side" && (
          <ButtonIcon icon={External} variant="ghost" size="small" onPress={handleUnpin}>
            Unpin to floating
          </ButtonIcon>
        )}
        <ButtonIcon icon={Cross} onPress={() => setIsOpen(false)} variant="ghost">
          Close AI chat
        </ButtonIcon>
      </Box>
    </Box>
  );
};

export default AssistantPanelLayoutHeader;
